import React, { createContext, useContext, useState, useEffect } from 'react';
import translations from '../locales/translations.json';

// Create the Language Context
const LanguageContext = createContext();

// Available languages
export const LANGUAGES = {
  en: { code: 'en', name: 'English', flag: '🇺🇸' },
  th: { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  la: { code: 'la', name: 'ລາວ', flag: '🇱🇦' }
};

// Default language
const DEFAULT_LANGUAGE = 'en';

// Language Context Provider
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  
  // Load language from localStorage on component mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && LANGUAGES[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('selectedLanguage', currentLanguage);
    
    // Optional: Set document language attribute for accessibility
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Change language function
  const changeLanguage = (languageCode) => {
    if (LANGUAGES[languageCode]) {
      setCurrentLanguage(languageCode);
      console.log(`🌐 Language changed to: ${LANGUAGES[languageCode].name}`);
    } else {
      console.warn(`⚠️ Unsupported language code: ${languageCode}`);
    }
  };

  // Get translation function with enhanced fallback
  const getTranslation = (key, defaultValue = key) => {
    try {
      const keys = key.split('.');
      
      // Try current language first
      let translation = translations[currentLanguage];
      let found = true;
      
      for (const k of keys) {
        if (translation && typeof translation === 'object' && translation[k]) {
          translation = translation[k];
        } else {
          found = false;
          break;
        }
      }
      
      if (found && typeof translation === 'string') {
        return translation;
      }
      
      // Fallback to English if translation not found in current language
      if (currentLanguage !== 'en') {
        translation = translations['en'];
        found = true;
        
        for (const k of keys) {
          if (translation && typeof translation === 'object' && translation[k]) {
            translation = translation[k];
          } else {
            found = false;
            break;
          }
        }
        
        if (found && typeof translation === 'string') {
          console.warn(`🔍 Translation fallback to English for key: ${key} (${currentLanguage} not available)`);
          return translation;
        }
      }
      
      // Final fallback to default value (only if defaultValue is not null)
      if (defaultValue !== null) {
        console.warn(`⚠️ Translation missing for key: ${key} in all available languages`);
        return defaultValue;
      }
      
      // Return null to indicate no translation found
      return null;
      
    } catch (error) {
      console.error(`❌ Error getting translation for key: ${key}`, error);
      return defaultValue;
    }
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return LANGUAGES[currentLanguage];
  };

  // Check if language is RTL (Right-to-Left)
  const isRTL = () => {
    // Currently none of our languages are RTL, but this can be extended
    return false;
  };

  // Get all available languages
  const getAvailableLanguages = () => {
    return Object.values(LANGUAGES);
  };

  // Development utility: Check for missing translations
  const checkMissingTranslations = () => {
    if (process.env.NODE_ENV !== 'development') return;
    
    const missingTranslations = {};
    const allLanguages = Object.keys(translations);
    
    // Helper function to flatten nested objects
    const flatten = (obj, prefix = '') => {
      const flattened = {};
      for (const key in obj) {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          Object.assign(flattened, flatten(obj[key], prefix + key + '.'));
        } else {
          flattened[prefix + key] = obj[key];
        }
      }
      return flattened;
    };
    
    // Get all keys from English (base language)
    const englishKeys = Object.keys(flatten(translations.en));
    
    // Check each language for missing keys
    allLanguages.forEach(lang => {
      if (lang === 'en') return; // Skip base language
      
      const langKeys = Object.keys(flatten(translations[lang]));
      const missing = englishKeys.filter(key => !langKeys.includes(key));
      
      if (missing.length > 0) {
        missingTranslations[lang] = missing;
      }
    });
    
    if (Object.keys(missingTranslations).length > 0) {
      console.group('🌐 Missing Translations Report');
      Object.keys(missingTranslations).forEach(lang => {
        console.warn(`${LANGUAGES[lang].flag} ${LANGUAGES[lang].name}:`, missingTranslations[lang]);
      });
      console.groupEnd();
    } else {
      console.log('✅ All translations are complete!');
    }
    
    return missingTranslations;
  };

  const contextValue = {
    currentLanguage,
    changeLanguage,
    getTranslation,
    getCurrentLanguageInfo,
    isRTL,
    getAvailableLanguages,
    checkMissingTranslations,
    t: getTranslation, // Shorthand alias for getTranslation
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the Language Context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
};

// Higher-order component for class components
export const withLanguage = (WrappedComponent) => {
  return function LanguageHOC(props) {
    const languageProps = useLanguage();
    return <WrappedComponent {...props} {...languageProps} />;
  };
};

export default LanguageContext;